
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchManyToManyFunc,
	makeJoinEqualsFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserPaymentMethod from 'Models/Security/Acl/UserPaymentMethod';
import VisitorsPaymentMethod from 'Models/Security/Acl/VisitorsPaymentMethod';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IPaymentMethodEntityAttributes extends IModelAttributes {
	stripeCustomerId: string;
	stripePaymentMethodId: string;
	expiryDate: Date;

	businessEntitys: Array<
		| Models.BusinessEntityPaymentMethod
		| Models.IBusinessEntityPaymentMethodAttributes
	>;
	createdbyId: string;
	createdby: Models.UserEntity | Models.IUserEntityAttributes;

}

@entity('PaymentMethodEntity', 'Payment Method')
export default class PaymentMethodEntity extends Model
	implements IPaymentMethodEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserPaymentMethod(),
		new VisitorsPaymentMethod(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<PaymentMethodEntity, string>()
	@CRUD({
		name: 'Stripe Customer Id',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public stripeCustomerId: string;

	@observable
	@attribute<PaymentMethodEntity, string>()
	@CRUD({
		name: 'Stripe Payment Method Id',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public stripePaymentMethodId: string;

	@observable
	@attribute<PaymentMethodEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'Expiry Date',
		displayType: 'datepicker',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public expiryDate: Date;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Business Entity',
		displayType: 'reference-multicombobox',
		order: 40,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.BusinessEntityPaymentMethod,
		optionEqualFunc: makeJoinEqualsFunc('businessEntityId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'paymentMethodEntity',
			oppositeEntityName: 'businessEntity',
			relationName: 'paymentMethod',
			relationOppositeName: 'businessEntity',
			entity: () => Models.PaymentMethodEntity,
			joinEntity: () => Models.BusinessEntityPaymentMethod,
			oppositeEntity: () => Models.BusinessEntity,
		}),
		
	})
	public businessEntitys: Models.BusinessEntityPaymentMethod[] = [];

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'CreatedBy',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.UserEntity,
		
	})
	public createdbyId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public createdby: Models.UserEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IPaymentMethodEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IPaymentMethodEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.stripeCustomerId !== undefined) {
				this.stripeCustomerId = attributes.stripeCustomerId;
			}
			if (attributes.stripePaymentMethodId !== undefined) {
				this.stripePaymentMethodId = attributes.stripePaymentMethodId;
			}
			if (attributes.expiryDate !== undefined) {
				if (attributes.expiryDate === null) {
					this.expiryDate = attributes.expiryDate;
				} else {
					this.expiryDate = moment(attributes.expiryDate).toDate();
				}
			}
			if (attributes.businessEntitys !== undefined && Array.isArray(attributes.businessEntitys)) {
				for (const model of attributes.businessEntitys) {
					if (model instanceof Models.BusinessEntityPaymentMethod) {
						this.businessEntitys.push(model);
					} else {
						this.businessEntitys.push(new Models.BusinessEntityPaymentMethod(model));
					}
				}
			}
			if (attributes.createdbyId !== undefined) {
				this.createdbyId = attributes.createdbyId;
			}
			if (attributes.createdby !== undefined) {
				if (attributes.createdby === null) {
					this.createdby = attributes.createdby;
				} else if (attributes.createdby instanceof Models.UserEntity) {
					this.createdby = attributes.createdby;
					this.createdbyId = attributes.createdby.id;
				} else {
					this.createdby = new Models.UserEntity(attributes.createdby);
					this.createdbyId = this.createdby.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntitys {
			${Models.BusinessEntityPaymentMethod.getAttributes().join('\n')}
			businessEntity {
				${Models.BusinessEntity.getAttributes().join('\n')}
			}
		}
		createdby {
			${Models.UserEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			businessEntitys: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'businessEntitys',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(PaymentMethodEntity.prototype, 'created');
CRUD(modifiedAttr)(PaymentMethodEntity.prototype, 'modified');
